<script lang="ts" setup>
import { ref } from 'vue';
import useModal, { ModalTypes } from '../../composables/useModal';
import Modal from '../Modal.vue';
import Duplicate from './Duplicate.vue';
import { useLocale } from '../../composables/useLocale';
import { Conflict, Duplicates } from './types';
import { Prettify } from '../../types';
import { UserToCreate } from '../NewUser/useCreateMultipleUsers';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { CompleteEvent } from './index.vue';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  conflicts: Conflict[];
  currentConflict: Duplicates;
};
const props = defineProps<Props>();
const emits = defineEmits<{
  (event: 'resolveCurrentConflict', value: CompleteEvent): void;
}>();

const { isOpen, close } = useModal(props.name);

const conflictsResolved = ref(false);
const removeUsers = ref<UserToCreate[]>([]);
const useInstead = ref<Record<string, Prettify<CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]>>>({});

const reset = () => {
  conflictsResolved.value = false;
  currentComponentRef.value = null;
  removeUsers.value = [];
  useInstead.value = {};
};

const onUpdate = (value: UserToCreate, action: 'add' | 'remove') => {
  const index = removeUsers.value.findIndex((e) => e.key === value.key);
  if (action === 'add' && index === -1) {
    removeUsers.value.push(value);
  }

  if (action === 'remove' && index > -1) {
    removeUsers.value.splice(index, 1);
  }
};

const resolveCurrentConflict = () => {
  emits('resolveCurrentConflict', {
    removeUsers: removeUsers.value,
    resolvedKeys: props.currentConflict.users.map((u: UserToCreate) => u.key),
    propertyComplete: {},
    useInstead: {},
  });

  reset();
  close();
};

const currentComponentRef = ref();
</script>
<template>
  <Modal
    :name="name"
    :title="t('lcp.user_already_exists', { name: currentConflict ? currentConflict.users[0].name : '' })"
    :is-open="isOpen"
    :actionLabel="t('lcp.done')"
    :action="resolveCurrentConflict"
    :show-cancel-action="false"
    @close="reset"
  >
    <Duplicate
      v-if="currentConflict && currentConflict.type === 'duplicate'"
      ref="currentComponentRef"
      :key="currentConflict.keys"
      :duplicate-users="currentConflict.users"
      @update="onUpdate"
      :disabled="false"
      hide-title
    />
  </Modal>
</template>
